(function () {
  const enableAutoplay =
    typeof window.enableAutoplay !== 'undefined' ? window.enableAutoplay : false;

  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.mainImages = Array.from(container.querySelectorAll('.shop-main-image'));
      this.thumbnails = Array.from(container.querySelectorAll('.shop-thumbnail'));
      this.dots = Array.from(container.querySelectorAll('.shop-dot'));
      this.desktopPrevArrow = container.querySelector('.shop-desktop-prev-arrow');
      this.desktopNextArrow = container.querySelector('.shop-desktop-next-arrow');
      this.mobilePrevArrows = Array.from(container.querySelectorAll('.shop-mobile-prev-arrow'));
      this.mobileNextArrows = Array.from(container.querySelectorAll('.shop-mobile-next-arrow'));
      this.indicatorsContainer = container.querySelector('.shop-indicators, #thumbnail-indicators');
      this.mediaMapEl =
        container.querySelector('.product-variant-media-map') ||
        container.closest('.shop-carousel-container')?.querySelector('.product-variant-media-map');
      this.mediaMap = window.VariantMediaGroups?.parseMapFromElement(this.mediaMapEl);
      this.currentIndex = 0;
      this.maxIndex = this.mainImages.length - 1;
      this.init();
    }

    init() {
      const firstVisible = this.getVisibleIndices()[0] ?? 0;
      this.changeImage(firstVisible, { skipFilter: true });
      this.createIndicators();
      this.attachEvents();
      this.handleVideoAutoplay(firstVisible);
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.isSwiping = false;
      this.swipeThreshold = 30;

      const imageContainer = this.container.querySelector('.shop-main-image-wrapper');
      if (imageContainer) {
        imageContainer.addEventListener(
          'touchstart',
          (e) => {
            this.touchStartX = e.changedTouches[0].screenX;
            this.isSwiping = true;
          },
          { passive: true }
        );
        imageContainer.addEventListener(
          'touchmove',
          (e) => {
            if (!this.isSwiping) return;
            const deltaX = e.changedTouches[0].screenX - this.touchStartX;
            if (Math.abs(deltaX) > this.swipeThreshold / 2) e.preventDefault();
          },
          { passive: false }
        );
        imageContainer.addEventListener(
          'touchend',
          (e) => {
            this.touchEndX = e.changedTouches[0].screenX;
            this.handleSwipeGesture();
            this.isSwiping = false;
          },
          { passive: true }
        );
      }
    }

    getMediaElement(mediaId) {
      const id = String(mediaId);
      return (
        this.container.querySelector(`.shop-thumbnail[data-media-id="${id}"]`) ||
        this.container.querySelector(`.shop-main-image[data-media-id="${id}"]`) ||
        this.container.querySelector(`.shop-image-zoom-wrapper[data-media-id="${id}"]`)
      );
    }

    getVisibleIndices() {
      return this.mainImages
        .map((img, index) => ({ img, index }))
        .filter(({ img }) => !this.isMediaHidden(img))
        .map(({ index }) => index);
    }

    isMediaHidden(el) {
      if (!el) return true;
      const wrapper = el.closest('.shop-image-zoom-wrapper');
      if (wrapper?.classList.contains('variant-media-hidden')) return true;
      if (el.classList.contains('variant-media-hidden')) return true;
      return false;
    }

    setMediaHidden(mediaId, hidden) {
      const id = String(mediaId);
      const thumbs = this.container.querySelectorAll(`.shop-thumbnail[data-media-id="${id}"]`);
      const dots = this.container.querySelectorAll(`.shop-dot[data-media-id="${id}"]`);
      const main = this.container.querySelector(`.shop-main-image[data-media-id="${id}"]`);
      const wrapper = this.container.querySelector(`.shop-image-zoom-wrapper[data-media-id="${id}"]`);

      thumbs.forEach((el) => el.classList.toggle('variant-media-hidden', hidden));
      dots.forEach((el) => el.classList.toggle('variant-media-hidden', hidden));
      if (main) main.classList.toggle('variant-media-hidden', hidden);
      if (wrapper) wrapper.classList.toggle('variant-media-hidden', hidden);
    }

    filterByVariant(variantId, preferredMediaId = null) {
      if (!this.mediaMap?.filterByVariant || !window.VariantMediaGroups) return;

      const visibleIds = window.VariantMediaGroups.getMediaIdsForVariant(variantId, this.mediaMap);
      const visibleSet = new Set(visibleIds.map(String));

      (this.mediaMap.mediaIds || []).forEach((mediaId) => {
        this.setMediaHidden(mediaId, !visibleSet.has(String(mediaId)));
      });

      const visibleIndices = this.getVisibleIndices();
      let targetIndex = visibleIndices[0] ?? 0;

      if (preferredMediaId) {
        const preferredIndex = this.mainImages.findIndex(
          (img) => String(img.dataset.mediaId) === String(preferredMediaId)
        );
        if (preferredIndex >= 0) {
          if (!visibleIndices.includes(preferredIndex)) {
            this.setMediaHidden(preferredMediaId, false);
            const refreshedVisible = this.getVisibleIndices();
            if (refreshedVisible.includes(preferredIndex)) {
              targetIndex = preferredIndex;
            }
          } else {
            targetIndex = preferredIndex;
          }
        }
      }

      this.changeImage(targetIndex, { skipFilter: true });
    }

    attachEvents() {
      this.thumbnails.forEach((thumb, i) => {
        thumb.addEventListener('click', () => {
          if (!this.isMediaHidden(thumb)) this.changeImage(i);
        });
      });

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          if (!this.isMediaHidden(dot)) this.changeImage(i);
        });
        dot.addEventListener('keydown', (e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !this.isMediaHidden(dot)) {
            e.preventDefault();
            this.changeImage(i);
          }
        });
      });

      const bindArrow = (arrow, direction) => {
        if (!arrow) return;
        const handler = () => this.stepVisible(direction);
        arrow.addEventListener('click', handler);
        arrow.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler();
          }
        });
      };

      bindArrow(this.desktopPrevArrow, -1);
      bindArrow(this.desktopNextArrow, 1);
      this.mobilePrevArrows.forEach((arrow) => bindArrow(arrow, -1));
      this.mobileNextArrows.forEach((arrow) => bindArrow(arrow, 1));

      if (this.indicatorsContainer) {
        this.indicatorsContainer.addEventListener('click', (e) => {
          if (e.target.classList.contains('shop-indicator-dot')) {
            const idx = parseInt(e.target.getAttribute('data-index'), 10);
            if (!isNaN(idx)) this.changeImage(idx);
          }
        });
      }
    }

    stepVisible(direction) {
      const visible = this.getVisibleIndices();
      if (!visible.length) return;

      const currentPos = visible.indexOf(this.currentIndex);
      const safePos = currentPos >= 0 ? currentPos : 0;
      const nextPos =
        direction < 0
          ? safePos === 0
            ? visible.length - 1
            : safePos - 1
          : safePos === visible.length - 1
            ? 0
            : safePos + 1;

      this.changeImage(visible[nextPos]);
    }

    createIndicators() {
      if (!this.indicatorsContainer) return;
      this.indicatorsContainer.innerHTML = '';
      this.mainImages.forEach((_, i) => {
        const dot = document.createElement('div');
        dot.className = 'shop-indicator-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('data-index', i);
        this.indicatorsContainer.appendChild(dot);
      });
    }

    changeImage(index, options = {}) {
      if (index < 0 || index > this.maxIndex) return;
      if (this.isMediaHidden(this.mainImages[index])) {
        const visible = this.getVisibleIndices();
        if (visible.length) index = visible[0];
      }

      this.mainImages.forEach((img, i) => {
        img.classList.toggle('active', i === index);
        const wrapper = img.closest('.shop-image-zoom-wrapper');
        if (wrapper) wrapper.classList.toggle('active', i === index);
      });

      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });

      if (this.indicatorsContainer) {
        Array.from(this.indicatorsContainer.children).forEach((dot, i) => {
          dot.classList.toggle('active', i === index);
        });
      }

      this.currentIndex = index;
      this.scrollToThumbnail(index);
      this.handleVideoAutoplay(index);
    }

    scrollToThumbnail(index) {
      const thumbnail = this.thumbnails[index];
      if (!thumbnail || this.isMediaHidden(thumbnail)) return;
      const container = thumbnail.parentElement;
      if (!container) return;
      const containerWidth = container.offsetWidth;
      const thumbnailWidth = thumbnail.offsetWidth;
      const thumbnailLeft = thumbnail.offsetLeft;
      const scrollPosition = thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;
      container.scrollTo({ left: scrollPosition, behavior: 'smooth' });
    }

    handleSwipeGesture() {
      const deltaX = this.touchEndX - this.touchStartX;
      if (Math.abs(deltaX) > this.swipeThreshold) {
        this.stepVisible(deltaX < 0 ? 1 : -1);
      }
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    handleVideoAutoplay(index) {
      this.mainImages.forEach((img) => {
        const video = img.querySelector('video');
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });

      const activeMedia = this.mainImages[index];
      if (!activeMedia) return;
      const videoToPlay = activeMedia.querySelector('video');
      if (videoToPlay && enableAutoplay) {
        videoToPlay.play().catch(() => {});
      }
    }

    updateWithVariant(variant) {
      if (!variant) return;

      if (variant.variantId && this.mediaMap?.filterByVariant) {
        this.filterByVariant(variant.variantId, variant.mediaId);
        return;
      }

      if (variant.mediaIds?.length) {
        const visibleSet = new Set(variant.mediaIds.map(String));
        (this.mediaMap?.mediaIds || []).forEach((mediaId) => {
          this.setMediaHidden(mediaId, !visibleSet.has(String(mediaId)));
        });
        const visibleIndices = this.getVisibleIndices();
        const featuredIndex = variant.mediaId
          ? this.mainImages.findIndex((img) => img.dataset.mediaId == variant.mediaId)
          : visibleIndices[0];
        this.changeImage(featuredIndex >= 0 ? featuredIndex : visibleIndices[0] ?? 0, {
          skipFilter: true,
        });
        return;
      }

      let matchIndex = -1;
      if (variant.mediaId || variant.variantId || variant.imageUrl) {
        this.mainImages.forEach((img, i) => {
          if (
            (variant.mediaId && img.dataset.mediaId == variant.mediaId) ||
            (variant.variantId && img.dataset.variantId == variant.variantId) ||
            (variant.imageUrl &&
              (img.src.includes(variant.imageUrl) ||
                (img.dataset.src && img.dataset.src.includes(variant.imageUrl))))
          ) {
            matchIndex = i;
          }
        });
      }

      if (matchIndex >= 0) {
        this.changeImage(matchIndex);
      } else {
        const visible = this.getVisibleIndices();
        this.changeImage(visible[0] ?? 0);
      }
    }
  }

  const galleryRegistry = [];

  function initAll() {
    document.querySelectorAll('.product-gallery').forEach((container) => {
      if (!container.__galleryInstance) {
        const gallery = new ProductGallery(container);
        container.__galleryInstance = gallery;
        galleryRegistry.push(gallery);
      }
    });
  }

  function handleVariantEvent(e) {
    if (!e.detail) return;
    if (e.detail.variantId || e.detail.mediaId || e.detail.imageUrl || e.detail.mediaIds) {
      galleryRegistry.forEach((gallery) => gallery.updateWithVariant(e.detail));
    }
  }

  document.addEventListener('variantImageSelected', handleVariantEvent);
  document.addEventListener('variant:imageChanged', handleVariantEvent);

  window.ProductGallery = {
    initAll,
    getAll: () => galleryRegistry,
  };

  document.addEventListener('DOMContentLoaded', initAll);
})();
